import React, { useState, useEffect, useMemo } from 'react';
import { Navigation } from './Navigation';
import { MapPin } from 'lucide-react';
import api from '../utils/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Treemap
} from 'recharts';

const formatDKK = (val) => Math.round(val).toLocaleString('da-DK');

const REGION_COLORS = {
  'Hovedstaden': '#1E6F7A',
  'Nordsjælland': '#0288D1',
  'Østjylland': '#2E7D32',
  'Midtjylland': '#8B5CF6',
  'Nordjylland': '#C77700',
  'Fyn': '#E91E63',
  'Sydsjælland': '#FF6F00',
  'Vestsjælland': '#546E7A',
  'Sønderjylland': '#00897B',
  'Sydvestjylland': '#5C6BC0',
  'Bornholm': '#D81B60',
};
const fallbackColor = '#1E6F7A';
const getColor = (region) => REGION_COLORS[region] || fallbackColor;

const defaultStart = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 12);
  return d.toISOString().slice(0, 10);
};
const defaultEnd = () => new Date().toISOString().slice(0, 10);

export const Omraaderentabilitet = () => {
  const [regions, setRegions] = useState([]);
  const [postnumre, setPostnumre] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/marketing/omraaderentabilitet?start_date=${startDate}&end_date=${endDate}`);
        setRegions(res.data.regions || []);
        setPostnumre(res.data.postnumre || []);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const overallAvg = useMemo(() => {
    if (!regions.length) return 0;
    const totalDB = regions.reduce((s, r) => s + r.total_db, 0);
    const totalOrdrer = regions.reduce((s, r) => s + r.antal_ordrer, 0);
    return totalOrdrer > 0 ? Math.round(totalDB / totalOrdrer) : 0;
  }, [regions]);

  const treemapData = useMemo(() => {
    return regions.map(r => ({
      name: r.region,
      size: r.total_db,
      gns_db: r.gns_db,
      antal: r.antal_ordrer,
      color: getColor(r.region)
    }));
  }, [regions]);

  const topPostnumre = useMemo(() => postnumre.slice(0, 15), [postnumre]);
  const bottomPostnumre = useMemo(() => [...postnumre].sort((a, b) => a.gns_db - b.gns_db).slice(0, 10), [postnumre]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 sm:p-6" data-testid="omraaderentabilitet">
      <Navigation />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-[#1E6F7A]" />
          <h1 className="text-xl sm:text-2xl font-bold">Områderentabilitet</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3" data-testid="date-picker">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-2 sm:px-3 py-1.5 text-xs sm:text-sm focus:outline-none focus:border-[#1E6F7A] flex-1 min-w-0"
            data-testid="start-date" />
          <span className="text-white/40 text-xs sm:text-sm">til</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-2 sm:px-3 py-1.5 text-xs sm:text-sm focus:outline-none focus:border-[#1E6F7A] flex-1 min-w-0"
            data-testid="end-date" />
        </div>
      </div>

      {loading ? (
        <div className="h-96 flex items-center justify-center text-white/40">Indlæser...</div>
      ) : (
        <div className="space-y-6">

          {/* Region overview cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="region-cards">
            {regions.map((r) => (
              <div key={r.region} className="bg-[#151515] rounded-xl p-4 border border-white/5 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full" style={{ background: getColor(r.region) }}></div>
                <p className="text-xs text-white/50 mb-1 pl-2">{r.region}</p>
                <p className="text-lg sm:text-xl font-bold pl-2" style={{ color: getColor(r.region) }}>{formatDKK(r.gns_db)} <span className="text-xs font-normal text-white/40">kr</span></p>
                <div className="flex items-center justify-between mt-2 pl-2">
                  <span className="text-[10px] text-white/40">{r.antal_ordrer} ordrer</span>
                  <span className="text-[10px] text-white/40">Total: {formatDKK(r.total_db)} kr</span>
                </div>
                {/* Relative bar */}
                <div className="mt-2 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                    width: `${overallAvg > 0 ? Math.min((r.gns_db / (overallAvg * 1.5)) * 100, 100) : 0}%`,
                    background: getColor(r.region)
                  }}></div>
                </div>
              </div>
            ))}
          </div>

          {/* Horizontal bar chart - regions sorted by avg DB */}
          <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="region-bar-chart">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
              <h2 className="text-sm font-medium text-white/80">Gns. dækningsbidrag pr. region</h2>
              <span className="text-xs text-white/40">Landsgennemsnit: {formatDKK(overallAvg)} kr</span>
            </div>
            <p className="text-xs text-white/30 mb-4">Sorteret efter højeste gennemsnitlige dækningsbidrag</p>
            <div className="-mx-2 sm:mx-0">
              <ResponsiveContainer width="100%" height={Math.max(250, regions.length * 45)}>
                <BarChart data={regions} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="region" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} width={100} />
                  <Tooltip
                    contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                    labelStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}
                    formatter={(value, name, props) => {
                      const item = props.payload;
                      return [`${formatDKK(value)} kr (${item.antal_ordrer} ordrer)`, 'Gns. DB'];
                    }}
                  />
                  <Bar dataKey="gns_db" radius={[0, 6, 6, 0]} barSize={28}>
                    {regions.map((r, i) => (
                      <Cell key={`cell-${i}`} fill={getColor(r.region)} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Treemap - total DB by region */}
          {treemapData.length > 0 && (
            <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="region-treemap">
              <h2 className="text-sm font-medium text-white/80 mb-1">Samlet dækning pr. region</h2>
              <p className="text-xs text-white/30 mb-4">Størrelsen viser total dækningsbidrag — jo større felt, jo mere DB fra regionen</p>
              <ResponsiveContainer width="100%" height={300}>
                <Treemap
                  data={treemapData}
                  dataKey="size"
                  aspectRatio={4/3}
                  stroke="rgba(0,0,0,0.3)"
                  content={({ x, y, width, height, name, size, gns_db, antal, color }) => {
                    if (width < 30 || height < 30) return <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.75} stroke="rgba(0,0,0,0.3)" />;
                    return (
                      <g>
                        <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.75} stroke="rgba(0,0,0,0.3)" rx={4} />
                        <text x={x + width / 2} y={y + height / 2 - 8} textAnchor="middle" fill="#fff" fontSize={width < 70 ? 10 : 12} fontWeight="600">{name}</text>
                        <text x={x + width / 2} y={y + height / 2 + 8} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={width < 70 ? 9 : 11}>{formatDKK(size)} kr</text>
                        {height > 50 && <text x={x + width / 2} y={y + height / 2 + 22} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize={9}>{antal} ordrer</text>}
                      </g>
                    );
                  }}
                />
              </ResponsiveContainer>
            </div>
          )}

          {/* Top & Bottom zip codes side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top 15 most profitable zip codes */}
            <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="top-postnumre">
              <h2 className="text-sm font-medium text-white/80 mb-1">Top 15 mest rentable postnumre</h2>
              <p className="text-xs text-white/30 mb-4">Postnumre med min. 3 ordrer, sorteret efter gns. DB</p>
              <div className="space-y-2">
                {topPostnumre.map((p, idx) => {
                  const maxDB = topPostnumre[0]?.gns_db || 1;
                  const pct = (p.gns_db / maxDB) * 100;
                  return (
                    <div key={p.postnummer} className="group">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/30 w-4">{idx + 1}</span>
                          <span className="text-sm text-white font-medium">{p.postnummer}</span>
                          <span className="text-xs text-white/40">{p.by}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-white/30">{p.antal_ordrer} ordrer</span>
                          <span className="text-sm font-semibold" style={{ color: getColor(p.region) }}>{formatDKK(p.gns_db)} kr</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden ml-6">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: getColor(p.region), opacity: 0.7 }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom 10 least profitable zip codes */}
            <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="bottom-postnumre">
              <h2 className="text-sm font-medium text-white/80 mb-1">10 mindst rentable postnumre</h2>
              <p className="text-xs text-white/30 mb-4">Postnumre med min. 3 ordrer, sorteret efter lavest gns. DB</p>
              <div className="space-y-2">
                {bottomPostnumre.map((p, idx) => {
                  const maxDB = topPostnumre[0]?.gns_db || 1;
                  const pct = (p.gns_db / maxDB) * 100;
                  return (
                    <div key={p.postnummer} className="group">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/30 w-4">{idx + 1}</span>
                          <span className="text-sm text-white font-medium">{p.postnummer}</span>
                          <span className="text-xs text-white/40">{p.by}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-white/30">{p.antal_ordrer} ordrer</span>
                          <span className="text-sm font-semibold text-[#EF5350]">{formatDKK(p.gns_db)} kr</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden ml-6">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: '#EF5350', opacity: 0.5 }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Region table */}
          <div className="bg-[#151515] rounded-xl p-4 sm:p-5 border border-white/5" data-testid="region-table">
            <h2 className="text-sm font-medium text-white/80 mb-4">Regionsoversigt</h2>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/60 border-b border-white/10">
                    <th className="text-left py-2 px-3">Region</th>
                    <th className="text-right py-2 px-3">Antal ordrer</th>
                    <th className="text-right py-2 px-3">Gns. DB</th>
                    <th className="text-right py-2 px-3">Total DB</th>
                    <th className="text-right py-2 px-3">Andel af total</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map((r, idx) => {
                    const totalAll = regions.reduce((s, rr) => s + rr.total_db, 0);
                    const pct = totalAll > 0 ? ((r.total_db / totalAll) * 100).toFixed(1) : '0';
                    return (
                      <tr key={r.region} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                        <td className="py-2 px-3 text-white flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm inline-block flex-shrink-0" style={{ background: getColor(r.region) }}></span>
                          {r.region}
                        </td>
                        <td className="py-2 px-3 text-right text-white/70">{r.antal_ordrer}</td>
                        <td className="py-2 px-3 text-right font-medium" style={{ color: getColor(r.region) }}>{formatDKK(r.gns_db)} kr</td>
                        <td className="py-2 px-3 text-right text-white/70">{formatDKK(r.total_db)} kr</td>
                        <td className="py-2 px-3 text-right text-white/50">{pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden space-y-3">
              {regions.map((r, idx) => {
                const totalAll = regions.reduce((s, rr) => s + rr.total_db, 0);
                const pct = totalAll > 0 ? ((r.total_db / totalAll) * 100).toFixed(1) : '0';
                return (
                  <div key={r.region} className="bg-white/[0.03] rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: getColor(r.region) }}></span>
                      <span className="text-sm font-medium text-white">{r.region}</span>
                      <span className="text-[10px] text-white/40 ml-auto">{pct}%</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-[10px] text-white/40">Ordrer</p>
                        <p className="text-sm text-white/80">{r.antal_ordrer}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40">Gns. DB</p>
                        <p className="text-sm font-medium" style={{ color: getColor(r.region) }}>{formatDKK(r.gns_db)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40">Total DB</p>
                        <p className="text-sm text-white/80">{formatDKK(r.total_db)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
